import { useState } from 'react'
import {
  Archive,
  CopySimple,
  PencilSimple,
  Plus,
  RocketLaunch,
  Trash,
  Tag,
} from '@phosphor-icons/react'
import { useFormat } from '@/i18n'
import { istDateKey } from '@/lib/ist'
import {
  Badge,
  Banner,
  Button,
  DataTable,
  EmptyState,
  Field,
  IconButton,
  Input,
  Modal,
  Select,
  type Column,
  type Tone,
} from '@/ui'
import { errorMessage } from '../api/core'
import { useCrops } from '../api/reference'
import {
  rateCardInForce,
  useDeleteRateCard,
  usePublishRateCard,
  useRateCard,
  useRateCards,
  useRetireRateCard,
  useSaveRateCard,
  type RateCardRow,
} from '../api/setup'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { DetailDrawer } from '../components/DetailDrawer'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage, QueryState } from '../components/Layout'
import { Facts, Kg, Money, Section, Stream, UserName, When } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'
import type { DeductionRule } from '../model/lotMath'
import {
  ACTION_LABEL,
  createBody,
  draftFromCard,
  emptyRateCardDraft,
  emptyRule,
  hasProblems,
  previewFarmerGets,
  rulesFromDraft,
  updateBody,
  validateRateCardDraft,
  type RateCardDraft,
  type RuleAction,
  type RuleDraft,
  type RuleField,
} from '../model/rateCardForm'
import { serverProblem } from '../model/serverError'
import { useScope } from '../api/appConfig'

const LIMIT = 50
const STREAMS = [
  { value: 'MAIN', label: 'Good' },
  { value: 'CONV', label: 'Ordinary' },
] as const
const STATUSES = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'RETIRED', label: 'Retired' },
] as const
const STATUS_TONE: Record<string, Tone> = { DRAFT: 'warn', PUBLISHED: 'ok', RETIRED: 'neutral' }
const streamWord = (s: string) => (s === 'CONV' ? 'ordinary' : 'good')

function CardStatus({
  card,
}: {
  card: Pick<RateCardRow, 'status' | 'effectiveFrom' | 'effectiveTo'>
}) {
  const label = STATUSES.find((s) => s.value === card.status)?.label ?? card.status
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Badge tone={STATUS_TONE[card.status] ?? 'neutral'}>{label}</Badge>
      {rateCardInForce(card) && <Badge tone="accent">In force today</Badge>}
    </span>
  )
}

function Dates({ from, to }: { from: string; to: string | null }) {
  const fmt = useFormat()
  return (
    <span className="whitespace-nowrap">
      {fmt.date(from)} – {to ? fmt.date(to) : <span className="text-muted">open</span>}
    </span>
  )
}

type Editor =
  | { mode: 'create'; draft: RateCardDraft }
  | { mode: 'edit'; id: string; version: number; cropName: string; draft: RateCardDraft }

/** Rate cards (F4.5-a/b): price per kg and moisture deductions by crop and stream, versioned. */
export function RateCardsPage() {
  const { v1 } = useScope()
  const [f, setF, { reset, active }] = useUrlState({
    cropId: '',
    stream: '',
    status: '',
    page: 1,
    open: '',
  })
  const q = useRateCards({
    page: f.page,
    limit: LIMIT,
    cropId: f.cropId || undefined,
    stream: f.stream || undefined,
    status: f.status || undefined,
  })
  const crops = useCrops()
  const [editor, setEditor] = useState<Editor | null>(null)
  const rows = q.data?.rows ?? []
  const newDraft = () =>
    setEditor({
      mode: 'create',
      draft: {
        ...emptyRateCardDraft(istDateKey()),
        cropId: f.cropId,
        stream: f.stream === 'CONV' ? 'CONV' : 'MAIN',
      },
    })

  const columns: Column<RateCardRow>[] = [
    {
      id: 'crop',
      header: 'Crop',
      cell: (r) => <span className="font-semibold">{r.cropName ?? '—'}</span>,
    },
    { id: 'stream', header: 'Stream', cell: (r) => <Stream stream={r.stream} /> },
    {
      id: 'version',
      header: 'Version',
      cell: (r) => `v${r.version}`,
      mono: true,
      align: 'right',
      width: '80px',
    },
    {
      id: 'price',
      header: '₹ per kg',
      align: 'right',
      cell: (r) => <Money paise={r.pricePaisePerKg} className="font-semibold" />,
    },
    {
      id: 'dates',
      header: 'In force',
      cell: (r) => <Dates from={r.effectiveFrom} to={r.effectiveTo} />,
      hideBelow: 'md',
    },
    ...(v1
      ? []
      : [
          {
            id: 'rules',
            header: 'Moisture',
            cell: (r: RateCardRow) =>
              r.deductionRules.length ? (
                `${r.deductionRules.length} band${r.deductionRules.length === 1 ? '' : 's'}`
              ) : (
                <span className="text-muted">No deductions</span>
              ),
            hideBelow: 'lg' as const,
          },
        ]),
    { id: 'status', header: 'Status', cell: (r) => <CardStatus card={r} /> },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Rate cards"
      description={
        v1
          ? 'The price per kg for each crop, good and red-tie. A card is prepared as a draft, then published; phones use the card in force on the day. V1 cards have no deductions.'
          : 'The price per kg and moisture deductions for each crop and stream. A card is prepared as a draft, then published; phones use the card in force on the day.'
      }
      actions={
        <ActionButton perm="rate-cards:create" leadingIcon={<Plus />} onClick={newDraft}>
          New draft card
        </ActionButton>
      }
    >
      <DataTable<RateCardRow>
        caption="Rate cards"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => setF({ open: r.id, page: f.page })}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        empty={
          <EmptyState
            icon={<Tag />}
            title={active ? 'No cards match these filters' : 'No rate cards yet'}
            description={
              active
                ? 'Clear the filters to see every card.'
                : 'Prepare a draft card for a crop and stream, then publish it.'
            }
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Crop"
              value={f.cropId}
              onChange={(cropId) => setF({ cropId })}
              options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <SelectFilter
              label="Stream"
              value={f.stream}
              onChange={(stream) => setF({ stream })}
              options={STREAMS}
            />
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={STATUSES}
            />
          </FilterBar>
        }
      />
      <RateCardDrawer
        id={f.open || null}
        onClose={() => setF({ open: '', page: f.page })}
        onEdit={(c) =>
          setEditor({
            mode: 'edit',
            id: c.id,
            version: c.version,
            cropName: c.cropName ?? 'card',
            draft: draftFromCard(c),
          })
        }
        onCopy={(c) =>
          setEditor({
            mode: 'create',
            draft: { ...draftFromCard(c), effectiveFrom: istDateKey(), effectiveTo: '' },
          })
        }
        onDeleted={() => setF({ open: '', page: f.page })}
      />
      {editor && (
        <RateCardEditor
          editor={editor}
          crops={crops.data ?? []}
          onClose={() => setEditor(null)}
          onCreated={(id) => setF({ open: id, page: f.page })}
        />
      )}
    </ConsolePage>
  )
}

// ------------------------------------------------------------------ detail drawer

function RateCardDrawer({
  id,
  onClose,
  onEdit,
  onCopy,
  onDeleted,
}: {
  id: string | null
  onClose: () => void
  onEdit: (c: RateCardRow) => void
  onCopy: (c: RateCardRow) => void
  onDeleted: () => void
}) {
  const q = useRateCard(id)
  const c = q.data?.rows[0] ?? null
  const [confirm, setConfirm] = useState<'publish' | 'retire' | 'delete' | null>(null)
  return (
    <DetailDrawer
      open={Boolean(id)}
      onClose={onClose}
      width="lg"
      eyebrow="Rate card"
      title={c ? `${c.cropName ?? 'Crop'} · ${streamWord(c.stream)} · v${c.version}` : 'Rate card'}
      meta={c && <CardStatus card={c} />}
      actions={
        c && (
          <>
            <ActionButton
              perm="rate-cards:create"
              variant="ghost"
              leadingIcon={<CopySimple />}
              onClick={() => onCopy(c)}
            >
              Copy to new draft
            </ActionButton>
            {c.status === 'DRAFT' && (
              <>
                <ActionButton
                  perm="rate-cards:delete"
                  variant="ghost"
                  leadingIcon={<Trash />}
                  onClick={() => setConfirm('delete')}
                >
                  Delete
                </ActionButton>
                <ActionButton
                  perm="rate-cards:update"
                  variant="secondary"
                  leadingIcon={<PencilSimple />}
                  onClick={() => onEdit(c)}
                >
                  Edit
                </ActionButton>
                <ActionButton
                  perm="rate-cards:publish"
                  leadingIcon={<RocketLaunch />}
                  onClick={() => setConfirm('publish')}
                >
                  Publish
                </ActionButton>
              </>
            )}
            {c.status === 'PUBLISHED' && (
              <ActionButton
                perm="rate-cards:retire"
                variant="danger"
                leadingIcon={<Archive />}
                onClick={() => setConfirm('retire')}
              >
                Retire
              </ActionButton>
            )}
          </>
        )
      }
    >
      <QueryState query={q} label="the rate card">
        {c && <RateCardBody c={c} />}
      </QueryState>
      {c && (
        <>
          {confirm === 'publish' && <PublishDialog card={c} onClose={() => setConfirm(null)} />}
          <RetireDialog card={c} open={confirm === 'retire'} onClose={() => setConfirm(null)} />
          <DeleteDialog
            card={c}
            open={confirm === 'delete'}
            onClose={() => setConfirm(null)}
            onDeleted={() => {
              setConfirm(null)
              onDeleted()
            }}
          />
        </>
      )}
    </DetailDrawer>
  )
}

function RateCardBody({ c }: { c: RateCardRow }) {
  const { v1 } = useScope()
  return (
    <div className="flex flex-col gap-5">
      <Facts
        columns={3}
        items={[
          { label: 'Crop', value: c.cropName },
          { label: 'Stream', value: <Stream stream={c.stream} /> },
          { label: 'Version', value: `v${c.version}`, mono: true },
          {
            label: 'Price per kg',
            value: <Money paise={c.pricePaisePerKg} className="text-[17px] font-semibold" />,
          },
          { label: 'In force', value: <Dates from={c.effectiveFrom} to={c.effectiveTo} /> },
          {
            label: 'Published',
            value: c.publishedAt ? (
              <span className="flex flex-col">
                <When at={c.publishedAt} />
                <span className="text-[13px] text-muted">
                  by {c.publishedByName ?? <UserName id={c.publishedById} />}
                </span>
              </span>
            ) : (
              <span className="text-muted">Not yet</span>
            ),
          },
        ]}
      />
      {(!v1 || c.deductionRules.length > 0) && (
        <>
          {v1 && (
            <Banner tone="warn">
              This card has moisture deductions. V1 phones will not buy against it: make a new draft
              without them.
            </Banner>
          )}
          <Section title="Moisture deductions" bodyClassName="p-0">
            <RulesTable rules={c.deductionRules} />
          </Section>
          <FarmerPreview rules={c.deductionRules} price={c.basePricePerKg} />
        </>
      )}
    </div>
  )
}

function deductionText(r: DeductionRule): string {
  if (r.action === 'ACCEPT') return 'Paid in full'
  if (r.action === 'DIVERT') return 'Not bought today — the farmer takes it home to dry'
  return `${r.deduct_pct_per_point ?? 0} % of the weight for each point above ${r.base_pct ?? 0} %`
}

const ACTION_TONE: Record<RuleAction, Tone> = { ACCEPT: 'ok', DEDUCT: 'warn', DIVERT: 'danger' }

function RulesTable({ rules }: { rules: ReadonlyArray<DeductionRule> }) {
  if (!rules.length)
    return (
      <p className="px-4 py-3 text-[14px] text-muted">
        No moisture deductions: every reading is paid in full.
      </p>
    )
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[14px]">
        <caption className="sr-only">Moisture bands</caption>
        <thead>
          <tr className="border-b border-rule text-left font-mono text-[10.5px] uppercase tracking-label text-muted">
            <th className="px-4 py-2 font-bold">Moisture</th>
            <th className="px-4 py-2 font-bold">Action</th>
            <th className="px-4 py-2 font-bold">What happens</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((r, i) => (
            <tr key={i} className="border-b border-rule last:border-0">
              <td className="whitespace-nowrap px-4 py-2 font-mono tabular">
                {r.from_pct} – {r.to_pct} %
              </td>
              <td className="px-4 py-2">
                <Badge tone={ACTION_TONE[r.action]}>{ACTION_LABEL[r.action]}</Badge>
              </td>
              <td className="px-4 py-2">{deductionText(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-rule px-4 py-2 text-[12.5px] text-muted">
        A reading outside every band is paid in full. Bands include both ends.
      </p>
    </div>
  )
}

const PREVIEW_G = 100_000

/** "A farmer at X % moisture gets …" for 100 kg net, worked out like the server does. */
function FarmerPreview({ rules, price }: { rules: ReadonlyArray<DeductionRule>; price: string }) {
  const [moisture, setMoisture] = useState('')
  const p = previewFarmerGets(rules, price, moisture, PREVIEW_G)
  return (
    <div className="rounded-card border border-rule bg-paper-tint px-4 py-3">
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Try a moisture reading" className="w-40">
          <Input
            inputMode="decimal"
            value={moisture}
            onChange={(e) => setMoisture(e.target.value)}
            placeholder="e.g. 13.5"
            trailing="%"
          />
        </Field>
        <div className="min-w-0 flex-1 pb-2 text-[14px]">
          {!moisture.trim() ? (
            <span className="text-muted">See what a farmer gets for 100 kg net.</span>
          ) : !p ? (
            <span className="text-danger">Enter a reading like 12 or 13.5.</span>
          ) : p.action === 'DIVERT' ? (
            <span>
              Band {p.band}: <strong>not bought today</strong> — the farmer takes it home to dry.
            </span>
          ) : (
            <span>
              For 100 kg net he is paid for <Kg grams={p.payableG} className="font-semibold" />
              {p.deductionPct !== '0.00' && <> (−{p.deductionPct} %)</>}:{' '}
              <Money paise={p.amountPaise} className="font-semibold" />
              <span className="text-muted"> · {p.band ? `band ${p.band}` : 'no band applies'}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ publish / retire / delete

function PublishDialog({ card, onClose }: { card: RateCardRow; onClose: () => void }) {
  const fmt = useFormat()
  const publish = usePublishRateCard()
  const current = useRateCards({
    page: 1,
    limit: 20,
    cropId: card.cropId ?? undefined,
    stream: card.stream,
    status: 'PUBLISHED',
  })
  const replaced = (current.data?.rows ?? []).filter(
    (o) => !o.effectiveTo || o.effectiveTo >= card.effectiveFrom,
  )
  return (
    <ConfirmDialog
      open
      onClose={onClose}
      title={`Publish ${card.cropName ?? ''} ${streamWord(card.stream)} v${card.version}?`}
      confirmLabel="Publish"
      loading={publish.isPending}
      onConfirm={async () => {
        await publish.mutateAsync(card.id)
        onClose()
      }}
    >
      <div className="flex flex-col gap-3 text-[14px] text-ink">
        <p>
          <Money paise={card.pricePaisePerKg} className="font-semibold" /> per kg from{' '}
          {fmt.date(card.effectiveFrom)}
          {card.effectiveTo ? ` to ${fmt.date(card.effectiveTo)}` : ', open ended'}.
        </p>
        {replaced.length > 0 ? (
          <ul className="flex flex-col gap-1 rounded-card border border-rule bg-paper-tint px-3 py-2">
            {replaced.map((o) => (
              <li key={o.id}>
                Replaces v{o.version} at <Money paise={o.pricePaisePerKg} />:{' '}
                {o.effectiveFrom < card.effectiveFrom
                  ? `it ends on ${fmt.date(prevDay(card.effectiveFrom))}.`
                  : 'it is retired.'}
              </li>
            ))}
          </ul>
        ) : (
          <p>It replaces the card in force for this crop and stream, if there is one.</p>
        )}
        <p>
          Phones pick up the new card with their next farmer list update. A published card cannot be
          edited — to change it, publish a new version.
        </p>
      </div>
    </ConfirmDialog>
  )
}

function prevDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

function RetireDialog({
  card,
  open,
  onClose,
}: {
  card: RateCardRow
  open: boolean
  onClose: () => void
}) {
  const retire = useRetireRateCard()
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={`Retire ${card.cropName ?? ''} ${streamWord(card.stream)} v${card.version}?`}
      confirmLabel="Retire card"
      tone="danger"
      loading={retire.isPending}
      onConfirm={async () => {
        await retire.mutateAsync(card.id)
        onClose()
      }}
    >
      <p className="text-[14px] text-ink">
        Phones stop using it with their next farmer list update. Until another card is published,
        deliveries of this crop in the {streamWord(card.stream)} stream have no price. Lots already
        weighed keep their price.
      </p>
    </ConfirmDialog>
  )
}

function DeleteDialog({
  card,
  open,
  onClose,
  onDeleted,
}: {
  card: RateCardRow
  open: boolean
  onClose: () => void
  onDeleted: () => void
}) {
  const del = useDeleteRateCard()
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={`Delete draft v${card.version}?`}
      description="The draft was never published; nothing on the phones changes."
      confirmLabel="Delete draft"
      tone="danger"
      loading={del.isPending}
      onConfirm={async () => {
        await del.mutateAsync(card.id)
        onDeleted()
      }}
    />
  )
}

// ------------------------------------------------------------------ editor

function RateCardEditor({
  editor,
  crops,
  onClose,
  onCreated,
}: {
  editor: Editor
  crops: ReadonlyArray<{ id: string; name: string }>
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const { v1 } = useScope()
  const [d, setD] = useState<RateCardDraft>(editor.draft)
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const save = useSaveRateCard({ inline: true })
  const problems = validateRateCardDraft(d, editor.mode)
  const invalid = hasProblems(problems)
  const touch = (k: string) => setTouched((s) => (s.has(k) ? s : new Set(s).add(k)))
  const shown = (k: string, msg: string | undefined) => (touched.has(k) ? msg : undefined)
  const set = (patch: Partial<RateCardDraft>) => setD((x) => ({ ...x, ...patch }))
  const setRule = (key: string, patch: Partial<RuleDraft>) =>
    setD((x) => ({ ...x, rules: x.rules.map((r) => (r.key === key ? { ...r, ...patch } : r)) }))
  const err = save.isError ? serverProblem(save.error) : null

  const submit = async () => {
    if (invalid) return
    try {
      if (editor.mode === 'edit') {
        await save.mutateAsync({ id: editor.id, body: updateBody(d) })
      } else {
        const created = (await save.mutateAsync({ body: createBody(d) })) as
          { id?: string } | undefined
        if (created?.id) onCreated(created.id)
      }
      onClose()
    } catch {
      /* shown in the dialog */
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!save.isPending}
      className="sm:max-w-3xl"
      title={
        editor.mode === 'edit'
          ? `Edit draft · ${editor.cropName} v${editor.version}`
          : 'New draft card'
      }
      description="A draft changes nothing on the phones until it is published."
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={invalid} onClick={() => void submit()}>
            {editor.mode === 'edit' ? 'Save draft' : 'Create draft'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {err && (
          <Banner tone="error" title={err.message}>
            {err.details.length > 0 && (
              <ul className="list-disc pl-5">
                {err.details.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            )}
          </Banner>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {editor.mode === 'create' ? (
            <>
              <Field label="Crop" required error={shown('crop', problems.cropId)}>
                <Select
                  value={d.cropId}
                  onChange={(cropId) => {
                    set({ cropId })
                    touch('crop')
                  }}
                  onBlur={() => touch('crop')}
                  placeholder="Choose…"
                  options={crops.map((c) => ({ value: c.id, label: c.name }))}
                />
              </Field>
              <Field label="Stream" required>
                <Select value={d.stream} onChange={(stream) => set({ stream })} options={STREAMS} />
              </Field>
            </>
          ) : null}
          <Field
            label="Price per kg"
            required
            error={shown('price', problems.price)}
            hint="Rupees, e.g. 62.00"
          >
            <Input
              inputMode="decimal"
              value={d.price}
              onChange={(e) => set({ price: e.target.value })}
              onBlur={() => touch('price')}
              leading={<span className="text-[15px]">₹</span>}
              trailing="/ kg"
              placeholder="62.00"
              data-autofocus={editor.mode === 'edit' || undefined}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3 sm:col-span-1">
            <Field label="From" required error={shown('from', problems.effectiveFrom)}>
              <Input
                type="date"
                value={d.effectiveFrom}
                onChange={(e) => set({ effectiveFrom: e.target.value })}
                onBlur={() => touch('from')}
              />
            </Field>
            <Field label="To" error={problems.effectiveTo} hint="Empty = open ended">
              <Input
                type="date"
                value={d.effectiveTo}
                min={d.effectiveFrom || undefined}
                onChange={(e) => set({ effectiveTo: e.target.value })}
              />
            </Field>
          </div>
        </div>

        {(!v1 || d.rules.length > 0) && (
          <fieldset className="flex flex-col gap-3">
            {v1 && (
              <Banner tone="warn">
                V1 cards have no deductions: remove these bands, or the card cannot be published.
              </Banner>
            )}
            <div className="flex flex-wrap items-end justify-between gap-2">
              <legend className="text-[14px] font-semibold text-ink">Moisture bands</legend>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                leadingIcon={<Plus />}
                onClick={() => set({ rules: [...d.rules, emptyRule()] })}
              >
                Add band
              </Button>
            </div>
            {d.rules.length === 0 ? (
              <p className="rounded-card border border-dashed border-rule px-3 py-3 text-[14px] text-muted">
                No bands: every moisture reading is paid in full.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {d.rules.map((r, i) => (
                  <RuleRow
                    key={r.key}
                    n={i + 1}
                    rule={r}
                    errors={problems.rules[i] ?? {}}
                    touched={touched}
                    touch={touch}
                    onChange={(patch) => setRule(r.key, patch)}
                    onRemove={() => set({ rules: d.rules.filter((x) => x.key !== r.key) })}
                  />
                ))}
              </div>
            )}
            {problems.table.length > 0 && (
              <ul
                role="alert"
                className="list-disc rounded-card border border-danger/30 bg-danger-wash py-2 pl-8 pr-3 text-[13.5px] text-danger"
              >
                {problems.table.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            )}
            <p className="text-[12.5px] text-muted">
              Bands include both ends; the first matching band wins. Deduct takes (moisture − base
              %) × deduct % per point off the net weight.
            </p>
          </fieldset>
        )}
        {!invalid && d.rules.length > 0 && (
          <FarmerPreview rules={rulesFromDraft(d.rules)} price={d.price} />
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}

function RuleRow({
  n,
  rule,
  errors,
  touched,
  touch,
  onChange,
  onRemove,
}: {
  n: number
  rule: RuleDraft
  errors: Partial<Record<RuleField, string>>
  touched: Set<string>
  touch: (k: string) => void
  onChange: (patch: Partial<RuleDraft>) => void
  onRemove: () => void
}) {
  const k = (f: RuleField) => `${rule.key}.${f}`
  const err = (f: RuleField) => (touched.has(k(f)) ? errors[f] : undefined)
  const pct = (f: RuleField, label: string, placeholder: string) => (
    <Field label={label} error={err(f)} required>
      <Input
        inputMode="decimal"
        value={rule[f]}
        onChange={(e) => onChange({ [f]: e.target.value })}
        onBlur={() => touch(k(f))}
        placeholder={placeholder}
        trailing="%"
      />
    </Field>
  )
  return (
    <div className="rounded-card border border-rule bg-surface p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-[11px] font-bold uppercase tracking-label text-muted">
          Band {n}
        </span>
        <IconButton label={`Remove band ${n}`} icon={<Trash />} size="sm" onClick={onRemove} />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {pct('from', 'From', '0')}
        {pct('to', 'To', '12')}
        <Field label="Action" required>
          <Select<RuleAction>
            value={rule.action}
            onChange={(action) => onChange({ action })}
            options={(Object.keys(ACTION_LABEL) as RuleAction[]).map((a) => ({
              value: a,
              label: ACTION_LABEL[a],
            }))}
          />
        </Field>
        {rule.action === 'DEDUCT' && (
          <>
            {pct('base', 'Base', '12')}
            {pct('perPoint', 'Per point', '1')}
          </>
        )}
      </div>
    </div>
  )
}
